import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography
} from "@mui/material";
import { Box } from "@mui/system";
import { Drag, raise } from "@visx/drag";
import { LinearGradient } from "@visx/gradient";
import { Group } from "@visx/group";
import { Bar, Line } from "@visx/shape";
import { Text } from "@visx/text";
import * as d3 from "d3";
import { filter, isEmpty, reverse } from "lodash";
import { useEffect, useMemo, useState } from "react";
import useMergeResources from "../../../hooks/process/useMergeResources";
import useResourceGraph from "../../../hooks/process/useResourceGraph";
import generateCircles, { Circle } from "./generateCircles";

export type DragIProps = {
  assetId: string;
  width: number;
  height: number;
  onNodeClick: (node: any) => void;
};

const nodeHeight = 30;

const VisxDrag = ({ assetId, width, height, onNodeClick }: DragIProps) => {
  const { graph } = useResourceGraph(assetId);
  const [isTimeOut, setIsTimeOut] = useState(false);
  const { mergeResources } = useMergeResources(assetId);
  const [mergeContenders, setMergeContenders] = useState<any>(null);

  const [draggingItems, setDraggingItems] = useState<Circle[]>([]);

  useEffect(() => {
    if (width > 10 && height > 10 && graph)
      setDraggingItems(generateCircles({ nodes: graph.nodes, width, height }));
  }, [graph, width, height]);

  let simulation: any = useMemo(
    () =>
      d3
        .forceSimulation(graph?.nodes as any)
        .force(
          "invisible-link",
          d3
            .forceLink(filter(graph?.links, { display: false }) as any)
            .id((d: any) => d.id)
            .distance(100)
        )
        .force(
          "visible-link",
          d3
            .forceLink(filter(graph?.links, { display: true }) as any)
            .id((d: any) => d.id)
            .distance(300)
        )
        .force("charge", d3.forceManyBody().strength(-110))
        .force("center", d3.forceCenter(width / 2, height / 2)),
    [graph, height, width]
  );

  useEffect(() => {
    if (!draggingItems || isTimeOut) {
      return;
    }

    setTimeout(() => {
      setIsTimeOut(true);
    }, 7000);

    // Update the positions of the nodes and links based on the simulation
    setDraggingItems([...simulation.nodes()]);
  }, [simulation, draggingItems, isTimeOut]);

  if (draggingItems.length === 0 || width < 10) return null;

  return (
    <Box sx={{ maxWidth: "100%", maxHeight: "80vh", overflow: "auto" }}>
      {!isTimeOut && (
        <Box sx={{ minHeight: "30vh", mt: 5, mx: "auto" }}>
          <Typography variant="h6">Generating Graph...</Typography>
        </Box>
      )}
      <Dialog
        open={
          !isEmpty(mergeContenders) &&
          mergeContenders.source?.id?.trim() !== "" &&
          mergeContenders.target?.id?.trim() !== ""
        }
        onClose={() => setMergeContenders(null)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Merge {mergeContenders?.source?.id} into {mergeContenders?.target?.id}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            All of the operations of {mergeContenders?.source?.id} will be
            merged into {mergeContenders?.target?.id}. Are you sure you want to
            proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              mergeResources(
                mergeContenders?.source?.id,
                mergeContenders?.target?.id
              );
              setDraggingItems(
                draggingItems.filter(
                  (p) => p.id !== mergeContenders?.source?.id
                )
              );
              setMergeContenders(null);
            }}
          >
            Go ahead
          </Button>
          <Button
            color="error"
            onClick={() => setMergeContenders(null)}
            autoFocus
          >
            Nope! That was a mistake.
          </Button>
        </DialogActions>
      </Dialog>

      <svg
        width={width}
        height={height}
        style={{ display: isTimeOut ? "block" : "none" }}
      >
        <LinearGradient id="stroke" from="#ff00a5" to="#ffc500" />
        <rect fill="#fff" width={width} height={height} rx={14} />

        {draggingItems.map((d, i) => {
          const textWidth = d.id.length * 7;
          const nodeWidth = textWidth * 2 + nodeHeight;
          return (
            <Drag
              key={`drag-${d.id}`}
              width={width}
              height={height}
              x={d.x}
              y={d.y}
              onDragStart={() => {
                // svg follows the painter model
                // so we need to move the data item
                // to end of the array for it to be drawn
                // "on top of" the other data items
                setDraggingItems(raise(draggingItems, i));
              }}
              onDragMove={({ x = 0, y = 0, dx, dy }) => {
                setDraggingItems(
                  draggingItems.map((p, j) =>
                    p.id === d.id ? { ...p, x: x + dx, y: y + dy } : p
                  ) as Circle[]
                );
              }}
              onDragEnd={({ x = 0, y = 0, dx, dy }) => {
                const newDraggingItems = draggingItems.map((p, j) =>
                  p.id === d.id ? { ...p, x: x + dx, y: y + dy } : p
                ) as Circle[];
                setDraggingItems(newDraggingItems);

                //find the node 40 pixels away from the current position
                const node = reverse(newDraggingItems).find((p, j) => {
                  const potentialTargetNodeWidth: number = p.id.length * 7 * 2;
                  const sourceNodeWidth: number = d.id.length * 7 * 2;
                  const potentialTargetCoordinates = {
                    x: p.x + potentialTargetNodeWidth / 2,
                    y: p.y + nodeHeight / 2,
                  };
                  const sourceCoordinates = {
                    x: x + sourceNodeWidth / 2,
                    y: y + nodeHeight / 2,
                  };
                  const distance = Math.sqrt(
                    Math.pow(p.x - x, 2) + Math.pow(p.y - y, 2)
                  );

                  return distance < 40 && p.id !== d.id;
                });
                if (node) {
                  setMergeContenders({ source: d, target: node });
                  setDraggingItems(raise(draggingItems, i));
                }
              }}
            >
              {({ dragStart, dragEnd, dragMove, isDragging, x, y, dx, dy }) => (
                <Group >
                  <Bar
                    key={`dot-${d.id}`}
                    x={x || 0 - nodeWidth / 2}
                    y={y || 0 + nodeHeight / 2}
                    width={nodeWidth}
                    height={nodeHeight}
                    radius={14}
                    fill={"#fff"}
                    transform={`translate(${dx}, ${dy})`}
                    fillOpacity={0.8}
                    stroke={"#10b981"}
                    strokeWidth={isDragging ? 5 : 2}
                  />
                  <Bar
                    key={`dot-${d.id}-drag`}
                    x={x || 0 - nodeWidth / 2}
                    y={y || 0 + nodeHeight / 2}
                    width={20}
                    style={{ cursor: "grab" }}
                    onMouseMove={dragMove}
                    onMouseUp={dragEnd}
                    onMouseDown={dragStart}
                    onTouchStart={dragStart}
                    onTouchMove={dragMove}
                    onTouchEnd={dragEnd}
                    height={nodeHeight - 10}
                    fill={"#10b981"}
                    transform={`translate(${dx}, ${dy})`}
                    fillOpacity={0.8}
                    stroke={"#10b981"}
                    strokeWidth={isDragging ? 5 : 2}
                  />

                  <Text
                    key={`text-${d.id}`}
                    className="hand"
                    x={(x || 0) + nodeWidth / 2}
                    y={(y || 0) + nodeHeight / 2}
                    verticalAnchor="middle"
                    textAnchor="middle"
                    fontWeight={600}
                    fontSize={14}
                    fill="#10b981"
                    onClick={() => onNodeClick(d)}
                    transform={`translate(${dx}, ${dy})`}
                  >
                    {d.id}
                  </Text>
                </Group>
              )}
            </Drag>
          );
        })}
        {draggingItems &&
          graph?.links.map((link) => {
            const sourceNode = draggingItems.find(
              (node) => node.id === link.source.id
            );
            const targetNode = draggingItems.find(
              (node) => node.id === link.target.id
            );
            console.log({ link });

            if (!sourceNode || !targetNode) return null;
            if (!link.display) return null;

            const sourceNodeWidth = sourceNode?.id?.length * 7 * 2;
            const targetNodeWidth = targetNode?.id?.length * 7 * 2;

            const linkPoints = [
              {
                x: sourceNode.x + sourceNodeWidth / 2,
                y: sourceNode.y + nodeHeight / 2,
              },
              {
                x: targetNode.x + targetNodeWidth / 2,
                y: targetNode.y + nodeHeight / 2,
              },
            ];

            return (
              <Line
                key={`${link.source.id}-${link.target.id}`}
                // data={{  }}
                stroke="#10b981"
                strokeWidth="2"
                x1={linkPoints[0].x}
                y1={linkPoints[0].y}
                x2={linkPoints[1].x}
                y2={linkPoints[1].y}
              />
            );
          })}
      </svg>

      <style>{`
        

        svg {
          
          -webkit-user-select: none; /* Safari */
          -ms-user-select: none; /* IE 10 and IE 11 */
          user-select: none; 
        }
       
      `}</style>
    </Box>
  );
};

export default VisxDrag;
